import { Euler } from 'three';

import {
  AXEL_CHARACTER_ID,
  MHR_CHARACTER_ID,
  stripCharacterIdInstanceSuffix,
} from '../shared/freeCharacterMesh';

export type MotionEditorDefaultCharacter = {
  heroThumbnailPath: string;
  jobID: string;
  jointJsonPath: string;
  meshPath: string;
  thumbnailPath: string;
  rotationOffsets?: undefined;
};

/** Character and BVH are often authored Y-up; rotate for display in motion scenes. */
export const CHARACTER_ROTATION = new Euler(-Math.PI / 2, 0, 0);

/** Legacy user-uploaded characters are already oriented for the motion editor stage. */
export const LEGACY_CHARACTER_ROTATION = new Euler(0, 0, 0);

const LEGACY_CHARACTER_ID_PREFIX = 'char-generation';

export function isLegacyMotionEditorCharacterId(characterID: string) {
  return stripCharacterIdInstanceSuffix(characterID).startsWith(
    LEGACY_CHARACTER_ID_PREFIX,
  );
}

/** Legacy user-uploaded characters use `createdBy` values like `user-...`. */
export function isLegacyMotionEditorCharacterCreatedBy(createdBy: string) {
  return createdBy.startsWith('user-');
}

export function resolveIsLegacyMotionEditorCharacter({
  characterID,
  createdBy,
}: {
  characterID: string;
  createdBy?: string;
}) {
  if (isLegacyMotionEditorCharacterId(characterID)) {
    return true;
  }

  if (createdBy && isLegacyMotionEditorCharacterCreatedBy(createdBy)) {
    return true;
  }

  return false;
}

export function getMotionEditorCharacterRotation(isLegacyCharacter: boolean) {
  return isLegacyCharacter ? LEGACY_CHARACTER_ROTATION : CHARACTER_ROTATION;
}

export const PLAYER_FRAME_RATE = 30;
export const CAMERA_DISTANCE_MULTIPLIER = 2;
export const DEFAULT_CAMERA_POSITION: [number, number, number] = [0, 4, 10];

export const PLAYER_ID = 'motion-editor-player';

export const PLAYER_INITIAL_SETTINGS = {
  playback: {
    frameRate: PLAYER_FRAME_RATE,
  },
  scene: {
    showActorNameTags: true,
  },
  environment: {
    backdropHexColor: '#5e97c5',
    useBackgroundGradient: false,
    showGrid: true,
    gridCellSize: 0.075,
    gridColor: '#83BDDE',
  },
};

const DOMAIN_PREFIX =
  import.meta.env.VITE_API_DISAMBIGUATOR === '' ||
  import.meta.env.VITE_API_DISAMBIGUATOR === undefined
    ? 'https://resources.getcartwheel.com'
    : 'https://resources-staging.getcartwheel.com';

export const DEFAULT_FALLBACK_BVH_URLS = [
  `${DOMAIN_PREFIX}/share/motions/default-animations1.bvh`,
  `${DOMAIN_PREFIX}/share/motions/default-animations2.bvh`,
  `${DOMAIN_PREFIX}/share/motions/default-animations3.bvh`,
];

export const DEFAULT_CHARACTER: MotionEditorDefaultCharacter = {
  jobID: 'char-upload-Axel',
  heroThumbnailPath: `${DOMAIN_PREFIX}/character-uploads/FREE_CHARACTERS/char-upload-Axel/hero-thumbnail.png`,
  jointJsonPath: `${DOMAIN_PREFIX}/character-uploads/FREE_CHARACTERS/char-upload-Axel/character.json`,
  meshPath: `${DOMAIN_PREFIX}/character-uploads/FREE_CHARACTERS/char-upload-Axel/character.glb`,
  thumbnailPath: `${DOMAIN_PREFIX}/character-uploads/FREE_CHARACTERS/char-upload-Axel/thumbnail.png`,
};

const MOTION_EDITING_SUPPORTED_CHARACTER_IDS = new Set([
  AXEL_CHARACTER_ID,
  MHR_CHARACTER_ID,
]);

export const MOTION_EDITING_UNSUPPORTED_CHARACTER_MESSAGE =
  'Motion editing is only supported with Axel and MHR right now, please switch to a supported character to continue.';

export function isMotionEditingSupportedCharacterId(characterID: string) {
  return MOTION_EDITING_SUPPORTED_CHARACTER_IDS.has(
    stripCharacterIdInstanceSuffix(characterID),
  );
}

export function isMhrMotionEditingCharacterId(characterID: string) {
  return stripCharacterIdInstanceSuffix(characterID) === MHR_CHARACTER_ID;
}

/** Backend motion-editor jobs only support source clips up to this length (seconds). */
export const MOTION_EDITING_MAX_SOURCE_DURATION_SECONDS = 10;

export const MOTION_EDITING_LONG_SOURCE_MOTION_MESSAGE =
  'This motion is longer than 10 seconds. You can explore key pose editing, but regenerating motion is not supported yet for clips this long.';

export const PATH = '/motion-editor';
